import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

// Services
import { AppService } from '../app.service';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { Logger } from '../logger/logger.service';

// Modèles
import { Client } from 'src/app/@shared/models/client/client.model';
import { User } from 'src/app/@shared/models/user/user.model';

// Services
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';
import { ClientService } from '../client/client.service';

const CLASS_NAME = 'User';
const ENDPOINT = `/users/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'avatar', path: 'avatar/', childClass: 'File' },
]
const ROUTES: RouteParam[] = [
    { key: 'logout', path: '{id}/logout/' },
    { key: 'me', path: 'me/' },
]
const LOGGER = new Logger('User service');

@Injectable({
    providedIn: 'root'
})
export class UserService extends ServiceBase {
    private _appService = inject(AppService);
    private _authenticationService = inject(AuthenticationService);
    private _clientService = inject(ClientService);
    private _router = inject(Router);


    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    /**
     * Appel des données de l'utilisateur connecté
     */
    getMe$(): Observable<boolean> {
        LOGGER.debug('Chargement des données utilisateur');
        return this._authenticationService.refreshToken$().pipe(
            catchError(response => {
                if (response.status === 401) {
                    this._authenticationService.logout$().subscribe((loggedOut) => {
                        if (loggedOut) {
                            // Reouverture de la page de connexion
                            this._router.navigate(['/login']);
                        }
                    });
                }
                throw response;
            }),
            switchMap(() => this.httpClient.get<any>(this.routes.me)),
            switchMap((data: any) => {
                let user = new User(data);
                this._appService.user = user;

                LOGGER.debug(`Données utilisateur ${user.fullName} chargées`);

                // Depuis #173, le dossier n'est plus restauré ici : il est porté par
                // l'URL et résolu par `ClientContextGuard`. L'initialisation ne charge
                // donc que la liste des dossiers, dont dépendent le sélecteur et la
                // redirection automatique en mono-dossier.
                return this.loadClients$();
            }),
            map(() => { return true })
        )
    }

    public loadClients$(): Observable<Client[]> {
        return this._clientService.getMine$().pipe(
            map((clients: Client[]) => {
                this._appService.clients = clients;
                return clients;
            })
        );
    }

    public loadClients(): void {
        this.loadClients$().subscribe();
    }

    /**
     * Chargement des données de l'utilisateur
     *
     * @returns boolean Retourne vrai si l'utilisateur est connecté
     */
    public loadUserData$(): Observable<boolean> {
        // Tente toujours une reconnexion silencieuse via le HttpOnly cookie.
        // Si le cookie est absent ou expiré, getMe$() échoue proprement et retourne false.
        return this.getMe$().pipe(
            map(() => true),
            catchError(error => {
                LOGGER.error('Erreur lors du chargement des données utilisateur', error);
                return of(false);
            })
        );
    }

    public loadUserData(): void {
        this.loadUserData$().subscribe();
    }

    public logout$(): Observable<any> {
        return this.httpClient.post(this.routes.logout.replace('{id}', this._appService.user?.id.toString()), {}).pipe(
            switchMap(() => this._authenticationService.logout$())
        );
    }

    public uploadAvatar$(user: User, file: File): Observable<any> {
        let url = this.routes['single'].replace('{id}', user.id.toString()) + this.navigations['avatar'].path;
        return this.uploadFile$(file, url);
    }
}
